(() => {
  const phone = matchMedia('(max-width: 768px)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  function init(root) {
    const sheet = root.querySelector('[data-look-detail]');
    const track = root.querySelector('[data-looks-track]');
    if (!sheet || !track || sheet.dataset.initialized) return;
    sheet.dataset.initialized = 'true';
    const person = sheet.querySelector('[data-look-detail-person]');
    const products = sheet.querySelector('[data-look-detail-products]');
    const grip = sheet.querySelector('[data-look-detail-drag]');
    let library;
    let trigger;
    let generation = 0;
    let closeTimer;
    let pointer;
    let lastSwipe = -Infinity;

    const linkSemantics = () => track.querySelectorAll('.v-looks__photo-link').forEach(link => {
      if (phone.matches) {
        link.setAttribute('aria-haspopup', 'dialog');
        link.setAttribute('aria-controls', sheet.id);
        link.setAttribute('aria-expanded', 'false');
      } else {
        ['aria-haspopup', 'aria-controls', 'aria-expanded'].forEach(name => link.removeAttribute(name));
      }
    });
    linkSemantics();

    const load = () => library || (library = fetch(sheet.dataset.cutoutsUrl)
      .then(response => {
        if (!response.ok) throw new Error('Look image manifest: ' + response.status);
        return response.json();
      }).catch(error => { library = null; throw error; }));

    const finishClose = () => {
      sheet.close();
      sheet.removeAttribute('data-open');
      sheet.style.removeProperty('--look-drag');
      sheet.removeAttribute('data-dragging');
      products.setAttribute('aria-busy', 'false');
      if (trigger?.isConnected) {
        if (phone.matches) trigger.setAttribute('aria-expanded', 'false');
        trigger.focus({ preventScroll: true });
      }
    };
    const close = (instant = false) => {
      if (!sheet.open) return;
      generation++;
      clearTimeout(closeTimer);
      sheet.removeAttribute('data-open');
      if (instant || reduced.matches) finishClose();
      else closeTimer = setTimeout(finishClose, 360);
    };

    function viewImage(image, title) {
      const img = document.createElement('img');
      const url = new URL(image.src, location.href);
      const sized = width => {
        const copy = new URL(url);
        copy.searchParams.set('width', Math.min(width, image.width));
        copy.searchParams.set('quality', '80');
        return copy.href;
      };
      img.src = sized(480);
      img.srcset = [...new Set([240, 360, 480, 640, 800, 960, image.width])]
        .filter(width => width <= image.width).sort((a, b) => a - b)
        .map(width => sized(width) + ' ' + width + 'w').join(', ');
      img.sizes = '57vw';
      img.width = image.width;
      img.height = image.height;
      img.alt = title;
      img.decoding = 'async';
      return img;
    }

    async function open(link) {
      clearTimeout(closeTimer);
      trigger = link;
      trigger.setAttribute('aria-expanded', 'true');
      const ticket = ++generation;
      const frame = link.closest('[data-look-product]');
      const handles = [...new Set([frame.dataset.lookProduct, ...Array.from(frame.querySelectorAll('[data-look-dot]'), dot => dot.dataset.lookDot)])];
      const image = frame.querySelector('.v-looks__img').cloneNode();
      image.removeAttribute('class');
      image.removeAttribute('loading');
      image.sizes = '100vw';
      image.decoding = 'async';
      person.replaceChildren(image);
      products.replaceChildren();
      products.setAttribute('aria-busy', 'true');
      sheet.dataset.productCount = handles.length;
      sheet.style.removeProperty('--look-drag');
      sheet.showModal();
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (ticket === generation && sheet.open) sheet.setAttribute('data-open', '');
      }));
      try {
        const data = await load();
        if (ticket !== generation || !sheet.open) return;
        for (const handle of handles) {
          const card = root.querySelector('[data-look-card="' + CSS.escape(handle) + '"]');
          const source = card?.querySelector('.v-looks__card-title');
          const image = data.products[handle];
          if (!source || !image) continue;
          const product = document.createElement('div');
          product.className = 'v-lookdetail__product';
          product.dataset.product = handle;
          const view = document.createElement('a');
          view.className = 'v-lookdetail__view';
          view.href = source.href;
          view.dataset.view = image.side;
          view.appendChild(viewImage(image, source.textContent.trim()));
          const title = source.cloneNode(true);
          title.className = 'v-lookdetail__title';
          product.append(view, title);
          products.appendChild(product);
        }
        sheet.dataset.productCount = products.children.length;
      } catch (error) {
        if (ticket === generation) {
          const fallback = link.cloneNode();
          fallback.className = 'v-lookdetail__title';
          fallback.textContent = link.getAttribute('aria-label');
          products.replaceChildren(fallback);
        }
        console.warn('[Tenzen/look]', error);
      } finally {
        if (ticket === generation) products.setAttribute('aria-busy', 'false');
      }
    }

    track.addEventListener('pointerdown', event => { pointer = { x: event.clientX, y: event.clientY }; }, { passive: true });
    track.addEventListener('pointermove', event => {
      if (pointer && Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) > 10) lastSwipe = performance.now();
    }, { passive: true });
    const release = () => { pointer = null; };
    track.addEventListener('pointerup', release, { passive: true });
    track.addEventListener('pointercancel', release, { passive: true });
    track.addEventListener('click', event => {
      const link = event.target.closest('.v-looks__photo-link');
      if (!phone.matches || !link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (event.detail && performance.now() - lastSwipe < 350) return;
      open(link);
    });
    sheet.querySelector('[data-look-detail-close]').addEventListener('click', () => close());
    sheet.addEventListener('cancel', event => { event.preventDefault(); close(); });
    sheet.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const stops = Array.from(sheet.querySelectorAll('button, a[href]'));
      const next = event.shiftKey ? stops.at(-1) : stops[0];
      const edge = event.shiftKey ? stops[0] : stops.at(-1);
      if (document.activeElement === edge) {
        event.preventDefault();
        next.focus();
      }
    });
    sheet.addEventListener('click', event => {
      if (event.target !== sheet) return;
      const box = sheet.getBoundingClientRect();
      if (event.clientY < box.top || event.clientX < box.left || event.clientX > box.right) close();
    });

    let dragStart;
    grip.addEventListener('pointerdown', event => {
      if (event.target.closest('button')) return;
      dragStart = event.clientY;
      grip.setPointerCapture(event.pointerId);
      sheet.setAttribute('data-dragging', '');
    });
    grip.addEventListener('pointermove', event => {
      if (dragStart !== undefined) sheet.style.setProperty('--look-drag', Math.max(0, event.clientY - dragStart) + 'px');
    });
    const endDrag = event => {
      if (dragStart === undefined) return;
      const distance = event.clientY - dragStart;
      dragStart = undefined;
      sheet.removeAttribute('data-dragging');
      sheet.style.removeProperty('--look-drag');
      if (distance > 70 && event.type !== 'pointercancel') close();
    };
    grip.addEventListener('pointerup', endDrag);
    grip.addEventListener('pointercancel', endDrag);
    phone.addEventListener('change', () => { if (!phone.matches) close(true); linkSemantics(); });
    window.addEventListener('pagehide', () => close(true));
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        if (phone.matches && entries.some(entry => entry.isIntersecting)) {
          load().catch(() => {});
          observer.disconnect();
        }
      }, { rootMargin: '300px' });
      observer.observe(track);
    }
  }

  document.querySelectorAll('.shopify-section:has([data-looks])').forEach(init);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
